import { useMemo, useState } from "react";
import { PageHeader } from "@/components/clinical/PageHeader";
import { JournalImportPanel } from "@/components/clinical/JournalImportPanel";
import { PsyLogImporter } from "@/components/clinical/PsyLogImporter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, CheckCircle2, XCircle, TableProperties } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "@/lib/safeToast";
import {
  importPsyLogCsv,
  importVitalSignsCsv,
  importSessionsCsv,
  importTargetSymptomsCsv,
  type ImportResult,
} from "@/lib/clinicalPersistence";

type Kind = "psylog" | "vital" | "sessions" | "symptoms";

const KINDS: { key: Kind; label: string; hint: string; headers: string }[] = [
  { key: "psylog", label: "Eventos PsyLog", hint: "patient_name, event_at, event_type, title, description, intensity_0_10, intensity_0_100, metadata", headers: "patient_name,event_at,event_type,title,description,intensity_0_10,intensity_0_100,metadata" },
  { key: "vital", label: "PA / sinais vitais", hint: "patient_name, measured_at, systolic_bp, diastolic_bp, heart_rate, weight_kg, oxygen_saturation, context, notes", headers: "patient_name,measured_at,systolic_bp,diastolic_bp,heart_rate,weight_kg,oxygen_saturation,context,notes" },
  { key: "sessions", label: "Sessões", hint: "patient_name, session_at, session_type, session_status, is_retroactive, notes", headers: "patient_name,session_at,session_type,session_status,is_retroactive,notes" },
  { key: "symptoms", label: "Sintomas-alvo", hint: "patient_name, symptom_name, baseline, therapeutic_goal, priority, related_diagnosis", headers: "patient_name,symptom_name,baseline,therapeutic_goal,priority,related_diagnosis" },
];

export default function ImportPsyLog() {
  const { user } = useAuth();
  const [kind, setKind] = useState<Kind>("psylog");
  const [text, setText] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const active = useMemo(() => KINDS.find((item) => item.key === kind)!, [kind]);

  async function runImport() {
    if (!text.trim()) return toast.error("Cole o CSV ou selecione um arquivo.");
    setBusy(true);
    setResult(null);
    try {
      const fn = { psylog: importPsyLogCsv, vital: importVitalSignsCsv, sessions: importSessionsCsv, symptoms: importTargetSymptomsCsv }[kind];
      const imported = await fn(user, text);
      setResult(imported);
      if (imported.failed === 0) toast.success(`${imported.inserted} linhas importadas.`);
      else toast.warning(`${imported.inserted} importadas; ${imported.failed} falharam.`);
    } catch (error: any) {
      toast.error(error?.message ?? String(error));
    } finally {
      setBusy(false);
    }
  }

  function loadFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ""));
    reader.readAsText(file);
  }

  function downloadTemplate() {
    const blob = new Blob([`${active.headers}\n`], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `template-${kind}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        title="Importar dados clínicos"
        description="DrugsPro Journal, CSV de eventos e PsyLog nativo com prévia, preservação temporal, deduplicação e reversão por lote."
      />

      <JournalImportPanel />

      <details className="rounded-xl border bg-card">
        <summary className="cursor-pointer list-none p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium">Importação nativa PsyLog</div>
            <div className="text-xs text-muted-foreground mt-1">Abra para exportações nativas com experiences, ingestões e sessões estruturadas.</div>
          </div>
          <Badge variant="outline">PsyLog</Badge>
        </summary>
        <div className="border-t p-3 md:p-5"><PsyLogImporter /></div>
      </details>

      <details className="rounded-xl border bg-card">
        <summary className="cursor-pointer list-none p-4 md:p-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium flex items-center gap-2"><TableProperties className="h-4 w-4" />Importação CSV avançada</div>
            <div className="text-xs text-muted-foreground mt-1">Use para PA, sessões, sintomas-alvo ou eventos tabulares no formato técnico do ClinicalOS.</div>
          </div>
          <Badge variant="outline">avançado</Badge>
        </summary>

        <div className="border-t p-4 md:p-5 space-y-4">
          <div className="flex flex-wrap gap-2">
            {KINDS.map((item) => (
              <Button key={item.key} size="sm" variant={kind === item.key ? "default" : "outline"} onClick={() => { setKind(item.key); setResult(null); }}>
                {item.label}
              </Button>
            ))}
          </div>

          <Card className="p-4 space-y-3 bg-muted/10">
            <div className="text-xs text-muted-foreground break-words">{active.hint}</div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex min-h-11 items-center gap-2 text-sm border rounded-md px-3 py-2 cursor-pointer hover:bg-muted">
                <Upload className="h-4 w-4" />Selecionar CSV
                <input type="file" accept=".csv,text/csv" className="hidden" onChange={loadFile} />
              </label>
              <Button variant="outline" size="sm" onClick={downloadTemplate}><FileText className="h-4 w-4 mr-1.5" />Baixar template</Button>
              <Button size="sm" onClick={runImport} disabled={busy || !text.trim()}>{busy ? "Importando…" : "Importar CSV"}</Button>
            </div>
            <Textarea rows={8} value={text} onChange={(event) => setText(event.target.value)} placeholder={`${active.headers}\n...`} className="font-mono text-xs" />
          </Card>

          {result ? (
            <Card className="p-4 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-success/10 text-success border-success/30" variant="outline"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{result.inserted} inseridos</Badge>
                <Badge className="bg-destructive/10 text-destructive border-destructive/30" variant="outline"><XCircle className="h-3.5 w-3.5 mr-1" />{result.failed} falharam</Badge>
              </div>
              {result.errors.length ? <div className="text-xs space-y-1 max-h-64 overflow-auto">{result.errors.map((error, index) => <div key={index} className="rounded border border-destructive/30 bg-destructive/5 p-2"><span className="font-mono">Linha {error.row}:</span> {error.message}</div>)}</div> : null}
            </Card>
          ) : null}
        </div>
      </details>
    </div>
  );
}
